import { Alert, Box, Button, CircularProgress, Divider, Grid, Link, Stack, Typography } from "@mui/material";
import { CardProps } from "..";
import WarningIcon from '@mui/icons-material/Warning';
import { useMemo, useState } from "react";
import { formatarValorMonetario } from "../../../../../../helpers/masks";
import SolicitarAnaliseCredito from "../../../../../Financeiro/components/SolicitarAnaliseCredito";
import { enqueueSnackbar } from "notistack";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { DadoValorPrinter } from "../../../../../../components/BasicComponents/DisplayDados/DadoValorPrinter";
import { useModalDetalheProcesso } from "../../../../../../components/ModalContexts/ModalDetalheProcessoContext";
import { usePermission } from "../../../../../../components/PermissionGuard";

export default function StepAprovacao({ data }: StepAprovacaoProps) {

    const queryClient = useQueryClient();
    const { openModal } = useModalDetalheProcesso()
    const permissao = usePermission("comercial.aprovar-pedidos")

    const [openSolicitarModal, setOpenSolicitarModal] = useState(false)

    const disabledBtn = useMemo(() => {
        return Number(data.publico.limite_disponivel) < ((data.total + data.frete)) || !permissao
    }, [data, permissao])

    //queries e mutations
    const { mutate: cadastrarProcesso, isLoading } = useMutation({
        mutationKey: 'cadastrar-processo-pedido',
        mutationFn: (input: string) => axiosClient.post("/comercial/pedidos/cadastrar-processo", {
            pedido_id: data.id,
            codigo_processo: input,
        }),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao vincular pedido ao processo!', { variant: 'error' });
        },
        onSuccess: ({ data }) => {
            enqueueSnackbar(`Pedido vinculado com o processo! ${data}`, { variant: 'success' });
            queryClient.invalidateQueries(`comercial/detalhar-pedido`);
        }
    })

    const { mutate: aprovarPedido, isLoading: loadingAprovar } = useMutation({
        mutationKey: 'aporovar-pedido',
        mutationFn: () => axiosClient.post(`/comercial/pedidos/aprovar-pedido/${data.id}`, {}),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao aprovar o pedido!', { variant: 'error' });
        },
        onSuccess: () => {
            enqueueSnackbar(`Pedido aprovado com sucesso!`, { variant: 'success' });
            queryClient.invalidateQueries(`comercial/detalhar-pedido`);
        }
    })
    //
    return (
        <>
            <Grid container spacing={2} mt={2}>
                <Grid size={{xs: 12}}>
                    {
                        !permissao &&
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Você não possui a permissão para aprovar pedidos.
                        </Alert>
                    }
                    {
                        data.processo &&
                        <Box sx={{ mx: 2, mb: 2 }}>
                            <Divider />
                            <Typography variant="subtitle1" fontWeight="medium" align="center" sx={{ my: 1 }} >
                                Detalhes do Processo
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                <Grid size={{xs: 12, md: 4}}>
                                    <Stack>
                                        <Typography fontWeight="bold" variant="subtitle2">Código do Processo</Typography>
                                        <Link
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                openModal(data.processo?.codigo ?? '')
                                            }}>
                                            <Typography variant="subtitle2">#{data.processo.codigo}</Typography>
                                        </Link>
                                    </Stack>
                                </Grid>
                                <Grid size={{xs: 12, md: 4}}>
                                    <DadoValorPrinter
                                        titulo="Status Atual"
                                        valor={data.processo.situacao}
                                    />
                                </Grid>
                                <Grid size={{xs: 12, md: 4}}>
                                    <DadoValorPrinter
                                        titulo="Tarefa Atual"
                                        valor={data.processo.situacao === "Finalizado" ? "-" : data.processo.tarefa_atual}
                                    />
                                </Grid>
                                <Grid size={{xs: 12}}>
                                    <DadoValorPrinter
                                        titulo="Justificativa"
                                        valor={!data.processo.ultima_tarefa.observacoes || data.processo.ultima_tarefa.observacoes === "" ? "-" : data.processo.ultima_tarefa.observacoes}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    }
                    <Box sx={{ mx: 2, pb: 2 }}>
                        {
                            Number(data.publico.limite_credito) < ((data.total + data.frete)) && !data.processo &&
                            <Alert
                                severity="warning"
                                icon={<WarningIcon fontSize="inherit" />}
                                sx={{ mb: 2 }}
                            >
                                Limite de crédito insuficiente para aprovar o pedido.
                            </Alert>

                        }
                        <Divider />
                        <Typography variant="subtitle1" fontWeight="medium" align="center" sx={{ my: 1 }} >
                            Limite de crédito
                        </Typography>
                        <Divider />

                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 1
                        }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Utilizado
                                </Typography>
                                <Typography variant="subtitle1" fontWeight="medium">
                                    {formatarValorMonetario(data.publico.limite_utilizado ?? 0)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" align="right">
                                    Disponível
                                </Typography>
                                <Typography variant="subtitle1" fontWeight="medium">
                                    {formatarValorMonetario(data.publico.limite_disponivel ?? 0)}
                                </Typography>
                            </Box>

                        </Box>
                        {
                            permissao &&
                            <Button
                                variant="outlined"
                                fullWidth
                                disabled={isLoading || Boolean(data.processo)}
                                onClick={() => setOpenSolicitarModal(true)}
                                sx={{ mt: 2 }}
                            >
                                Solicitar Análise de Crédito
                            </Button>
                        }
                    </Box>
                </Grid>
                <Grid size={{xs: 12}}>
                    <Button
                        variant="contained"
                        disabled={disabledBtn || isLoading || loadingAprovar}
                        color="primary"
                        onClick={() => aprovarPedido()}
                    >
                        {
                            loadingAprovar &&
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                        }
                        Aprovar
                    </Button>
                </Grid>
            </Grid>

            {
                openSolicitarModal &&
                <SolicitarAnaliseCredito
                    defaultClienteValue={data.publico}
                    handleChangeProcesso={(codigo: string) => {
                        enqueueSnackbar(`Solicitação de análise de crédito #${codigo} foi criada com sucesso!`, { variant: "success" })
                        cadastrarProcesso(codigo)
                    }}
                    open={openSolicitarModal}
                    handleClose={() => {
                        setOpenSolicitarModal(false)
                    }}
                    onCreate={(codigo: string) => { }}
                    onFail={() => {
                        enqueueSnackbar(`Erro ao criar solicitação de análise de crédito`, { variant: "error" })
                    }}
                />
            }
        </>
    )
}

interface StepAprovacaoProps extends CardProps { }
